import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import dayjs from "dayjs";
import extractoService from "../services/extractoService";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import ExtractoHistoricoTable from "../components/ExtractoHistoricoTable";
import EditExtractoModal from "../components/EditExtractoModal";
import RegisterExtractoModal from "../components/RegisterExtractoModal";

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 34 },
  "& .MuiInputBase-input": { py: "6px", fontSize: "0.82rem" },
  "& .MuiInputLabel-root": { fontSize: "0.82rem" },
};

const ExtractoHistoricoPage = () => {
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [data, setData] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openRegister, setOpenRegister] = useState(false);
  const [exportando, setExportando] = useState(false);

  useEffect(() => {
    const hoy = dayjs().format("YYYY-MM-DD");
    const primerDia = dayjs().startOf("month").format("YYYY-MM-DD");
    setFechaInicio(primerDia);
    setFechaFin(hoy);
  }, []);

  const handleBuscar = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await extractoService.getExtractoHistorico(fechaInicio, fechaFin);
      setData(result);
    } catch {
      setError("No se pudo cargar el extracto.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row) => {
    setSelectedRow(row);
    setEditOpen(true);
  };

  const handleUpdated = () => {
    handleBuscar();
  };

  const handleExportar = async () => {
    setExportando(true);
    setError("");
    try {
      const blob = await extractoService.exportarExcel(fechaInicio, fechaFin);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "extracto_historico.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError("No se pudo exportar el extracto a Excel.");
    } finally {
      setExportando(false);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Extracto Histórico"
        subtitle="Movimientos bancarios registrados"
        backTo="/dashboard"
        actions={(
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenRegister(true)}
            sx={{ textTransform: "none" }}
          >
            Registrar Nuevo
          </Button>
        )}
      />

      <Box sx={{ p: { xs: 1, md: 1.5 } }}>
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              size="small"
              label="Fecha Inicio"
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={compactFieldSx}
            />
            <TextField
              size="small"
              label="Fecha Fin"
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={compactFieldSx}
            />
            <Button size="small" variant="contained" onClick={handleBuscar} sx={{ textTransform: "none" }}>
              Buscar
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownloadIcon fontSize="small" />}
              disabled={exportando || !fechaInicio || !fechaFin}
              onClick={handleExportar}
              sx={{ textTransform: "none" }}
            >
              {exportando ? "Exportando..." : "Excel"}
            </Button>

            {data.length > 0 && (
              <TextField
                size="small"
                placeholder="Buscar en resultados..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ ...compactFieldSx, ml: "auto", minWidth: 220 }}
              />
            )}
          </Box>
        </Paper>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {!loading && data.length > 0 && (
          <Paper variant="outlined">
            <ExtractoHistoricoTable rows={data} filterText={filterText} onEdit={handleEdit} />
          </Paper>
        )}
      </Box>
      <EditExtractoModal
        open={editOpen}
        handleClose={() => setEditOpen(false)}
        data={selectedRow}
        onUpdated={handleUpdated}
      />
      <RegisterExtractoModal
        open={openRegister}
        handleClose={() => setOpenRegister(false)}
        onCreated={handleBuscar}
      />
    </DashboardLayout>
  );
};

export default ExtractoHistoricoPage;
